import {AppBar, Button, ButtonGroup, Divider, IconButton, Toolbar, useTheme} from '@mui/material';
import styles from './Header.module.scss';
import {useMemo, useRef} from 'react';
import UserAvatar from './UserAvatar';
import {getNavLinks} from './utils';
import {Routes} from 'constants/routes';
import {Link, NavLink, useLocation, useParams} from 'react-router-dom';
import {FILES_PAGE_ID, RouteParams, useModal} from 'utils';
import {WellSelect} from './WellSelect';
import {ColorModeToggle} from './ColorModeToggle';
import {FinalReportDialog} from '../FinalReport/FinalReportDialog';
import {Permission, usePermissions} from 'utils/hooks/permissions/usePermissions';
import useLogoCache from './useLogoCache';
import HeaderLogo from './HeaderLogo';
import {FullscreenToggleButton} from './FullScreenToggleButton';
import {MonitoringTabTypes} from '@pages/newMonitoring/components/NavTabs/types';
import {MonitoringWellSelect} from './MonitoringWellSelect';
import {ReportDownloadManager} from './ReportDownloadManager/ReportDownloadManager';
import {useTranslation} from 'react-i18next';
import {ChatInHeader} from '@features/Chat/ChatInHeader';
import {HeaderLogoBlock} from './HeaderLogoBlock';
import classNames from 'classnames';
import {FormatListBulletedOutlined} from '@mui/icons-material';
import {UserAvatarMobile} from './UserAvatarMobile';
import {useDeviceInfo} from 'utils/hooks/useDeviceInfo';
import {AppNavigation} from './AppNavigationButton/AppNavigation';

const buttonStyle = {
    '&.active': {
        color: 'text.primary',
        background: 'var(--mui-palette-buttonGroup-default)',
    },
    '&:hover:not(.active)': {
        color: 'text.primary',
    },
};

export const Header = () => {
    const {roles} = usePermissions();
    const modal = useModal();
    const reportButtonRef = useRef(null);
    const {wellId} = useParams();
    const theme = useTheme();
    const {logoSvg: companyLogoSvg} = useLogoCache(theme.palette.mode, 'company');
    const location = useLocation();
    const {t} = useTranslation(['header', 'common']);
    const {isMobile} = useDeviceInfo();

    const needTopControls = useMemo(() => {
        if (location.pathname === Routes.Main || location.pathname === Routes.Wells) {
            return false;
        }
        if (location.pathname.includes('/' + FILES_PAGE_ID)) {
            return true;
        }
        if (location.pathname.includes(Routes.DrillingRigInWell)) {
            return true;
        }
        return [
            Routes.CommonInfo,
            Routes.RatesAndPrices,
            Routes.Projects,
            Routes.Prices,
            Routes.LessonsDatabase,
            Routes.StageAnalytics,
            Routes.SMBO,
            Routes.AutoDrilling,
            Routes.DrillingCarpet,
            Routes.KnbkProject,
            Routes.Monitoring,
            Routes.Administration,
            Routes.MyResources,
            Routes.DrillingDates,
            Routes.Price,
            Routes.DrillingRig,
            Routes.WellsQuantity,
            Routes.DrillBitAnalytics,
            Routes.DataManager,
        ].every((v) => !location.pathname.startsWith(v));
    }, [location.pathname]);

    const needWells = useMemo(
        () =>
            !(
                location.pathname === Routes.Main ||
                location.pathname === Routes.Wells ||
                location.pathname === Routes.WellsQuantity ||
                location.pathname === Routes.WellsAnalytics ||
                location.pathname === Routes.SMBO ||
                location.pathname === Routes.DataManager ||
                location.pathname.includes(Routes.CommonInfo) ||
                location.pathname.includes(Routes.StageAnalytics) ||
                location.pathname.includes(Routes.Monitoring) ||
                location.pathname.includes(Routes.AutoDrilling) ||
                location.pathname.includes(Routes.MyResources) ||
                location.pathname.includes(Routes.DrillingDates) ||
                location.pathname.includes(Routes.Price)
            ),
        [location.pathname],
    );

    const needWellHeader = useMemo(() => {
        return (
            location.pathname === Routes.Main &&
            [Permission.CAN_VIEW_WELL_HEADER, Permission.CAN_EDIT_GRP].some((perm) =>
                roles?.includes(perm),
            )
        );
    }, [location.pathname, roles]);

    const params = useParams<RouteParams>();

    const navLinks = useMemo(() => getNavLinks(params.wellId), [params.wellId]);

    const needCompanyLogo = useMemo(() => {
        return roles?.includes(Permission.CAN_VIEW_ASB_LOGO);
    }, [roles]);

    const needWellsSelect = useMemo(() => {
        if (location.pathname.includes(MonitoringTabTypes.CANDLE_CONSTRUCTION)) {
            return false;
        }
        if (needTopControls) {
            return needTopControls;
        }
    }, [location.pathname, needTopControls]);

    const needMonitoringWellSelect = useMemo(() => {
        if (location.pathname === Routes.Monitoring) {
            return false;
        }
        return location.pathname.includes(Routes.Monitoring);
    }, [location.pathname]);

    const needReportDownloadManager = useMemo(() => {
        return !location.pathname.includes(Routes.MyResources);
    }, [location.pathname]);

    return (
        <>
            <AppBar
                className={styles.wrapper}
                enableColorOnDark
                sx={{
                    backgroundColor: 'var(--mui-palette-background-default)',
                    boxShadow: 'none',
                    width: '100%',
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                }}
                variant='outlined'
                elevation={0}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                        minHeight: '48px',
                    }}
                >
                    <HeaderLogoBlock />
                    <>
                        <div className={styles.wellInfoContainer}>
                            {needWells && (
                                <Link
                                    to={Routes.Wells}
                                    style={{flexShrink: 0}}
                                >
                                    <Button
                                        variant='outlined'
                                        size='large'
                                        sx={{alignSelf: 'end'}}
                                    >
                                        {t('allWells')}
                                    </Button>
                                </Link>
                            )}
                            {needWellsSelect && <WellSelect />}
                            {needMonitoringWellSelect && <MonitoringWellSelect />}
                        </div>
                        <div
                            className={classNames(styles.wellInfoContainer, {
                                [styles.wellInfoContainerMobile]: isMobile,
                            })}
                        >
                            {needWellHeader && (
                                <Link to={Routes.Wells}>
                                    {isMobile ? (
                                        <IconButton color='primary'>
                                            <FormatListBulletedOutlined />
                                        </IconButton>
                                    ) : (
                                        <Button
                                            variant='outlined'
                                            size='large'
                                            sx={{alignSelf: 'end'}}
                                        >
                                            {t('supervising')}
                                        </Button>
                                    )}
                                </Link>
                            )}
                        </div>

                        {needTopControls && roles?.includes(Permission.Wells) && (
                            <ButtonGroup
                                variant='text'
                                color='inherit'
                                sx={{
                                    marginX: 'auto',
                                    color: 'text.secondary',
                                }}
                            >
                                {navLinks.map((item) => (
                                    <Button
                                        key={item.link}
                                        component={NavLink}
                                        to={item.link}
                                        sx={buttonStyle}
                                    >
                                        {item.name}
                                    </Button>
                                ))}
                                <Button
                                    onClick={modal.show}
                                    ref={reportButtonRef}
                                    sx={buttonStyle}
                                >
                                    {t('reports')}
                                </Button>
                                {roles?.includes(Permission.CAN_VIEW_MONITORING) && wellId && (
                                    <Button
                                        sx={buttonStyle}
                                        component={NavLink}
                                        to={[Routes.Monitoring, wellId].join('/')}
                                    >
                                        {t('monitoring')}
                                    </Button>
                                )}
                            </ButtonGroup>
                        )}
                    </>

                    <div
                        className={classNames(styles.settingsBlock, {
                            [styles.settingsBlockMobile]: isMobile,
                        })}
                    >
                        <ChatInHeader />

                        {/* На мобильных устройствах эти кнопки будут в UserAvatar */}
                        {!isMobile && (
                            <>
                                {needReportDownloadManager && <ReportDownloadManager />}
                                <Divider
                                    orientation='vertical'
                                    flexItem
                                />
                                <FullscreenToggleButton />
                                <ColorModeToggle />
                            </>
                        )}
                        <AppNavigation />
                        {isMobile ? <UserAvatarMobile /> : <UserAvatar />}
                        {needCompanyLogo && <HeaderLogo logoSvg={companyLogoSvg} />}
                    </div>
                </Toolbar>
            </AppBar>
            {modal.isVisible && <FinalReportDialog onClose={modal.hide} />}
        </>
    );
};
